import type { MultipartUpload, UploaderArgs, UploadPartInput } from "./multipart-upload.types";
import { FailureReason, INVALID_DOCUMENT_ERROR, InvalidDocumentError } from "shared/multipart-upload/FailureReason";

const MAX_RETRIES = 2;

function isInvalidDocumentError(s: string): s is InvalidDocumentError {
  return (INVALID_DOCUMENT_ERROR as readonly string[]).indexOf(s) !== -1;
}

type UploadPart = {
  url: string;
  partIndex: number;
  partNumber: number;
  progress: number;
  state: "idle" | "inflight" | "aborted" | "failed" | "complete";
  etag: string | null;
  retries: number;
};

export abstract class Uploader {
  caseId: number;
  batchId: string;
  upload: MultipartUpload | undefined;
  preserveSectionStateForSections: number[];
  parts: UploadPart[] = [];
  file: File;
  onProgress: {
    (partIndex: number, partProgress: number, partTotal: number, totalSent: number): void;
  }[];
  onComplete: { (): void }[];
  onFailure: {
    (reason: Uploader["failureReason"], errorMessage?: string): void;
  }[];
  overallState: "failed" | "complete" | "inflight" | "queued";
  failureReason: FailureReason;

  constructor(args: UploaderArgs) {
    this.caseId = args.caseId;
    this.batchId = args.batchId;
    this.file = args.file;
    this.onProgress = [args.onProgress];
    this.onComplete = [args.onComplete];
    this.onFailure = [args.onFailure];
    this.overallState = "queued";
    this.preserveSectionStateForSections = args.preserveSectionStateForSections;
  }

  addCallbacks(callbacks: Partial<Pick<UploaderArgs, "onProgress" | "onFailure" | "onComplete">>) {
    if (callbacks.onProgress) this.onProgress.push(callbacks.onProgress);
    if (callbacks.onFailure) this.onFailure.push(callbacks.onFailure);
    if (callbacks.onComplete) this.onComplete.push(callbacks.onComplete);
  }

  retryPart(partIndex: number) {
    const part = this.parts[partIndex];
    if (part && part.retries < MAX_RETRIES) {
      part.state = "inflight";
      part.progress = 0;
    }
  }

  reportFailure(reason: Uploader["failureReason"], message?: string) {
    this.overallState = "failed";
    this.failureReason = reason;
    this.onFailure.forEach((onFailure) => onFailure(reason, message));
  }

  abstract sendBeginUpload(): Promise<MultipartUpload>;

  onBeginUploadSuccess(upload: MultipartUpload): MultipartUpload {
    if (upload === null) {
      this.reportFailure("unknown", "Unknown");
      throw new Error("Failed to begin upload");
    }
    this.overallState = "inflight";
    this.upload = upload;
    this.parts = upload.partUrls.map((partUrl, idx) => ({
      url: partUrl,
      partIndex: idx,
      partNumber: idx + 1,
      progress: 0,
      state: "idle",
      etag: null,
      retries: 0,
    }));
    this.parts.forEach((part) => {
      this.sendPart(part, upload.chunkSize);
    });
    return upload;
  }

  onBeginUploadFailure(status: number): Error {
    if (status === 401) {
      this.reportFailure("unauthorized", "Unauthorized");
      return Error("Unauthorized");
    } else {
      this.reportFailure("unknown", "Unknown");
      return Error("Failed to begin upload");
    }
  }

  sendPart(part: UploadPart, chunkSize: number) {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", part.url);
    return new Promise((_resolve, reject) => {
      xhr.upload.addEventListener("progress", (evt) => {
        part.progress = evt.loaded;
        const totalSent = this.getCurrentTotalSent();
        this.onProgress.forEach((onProgress) => onProgress(part.partIndex, evt.loaded, evt.total, totalSent));
      });
      xhr.addEventListener("error", () => {
        reject(new Error("Part failed"));
        part.state = "failed";
        this.retryPart(part.partIndex);
      });
      xhr.addEventListener("abort", () => {
        part.state = "aborted";
        reject(new Error("Part aborted"));
      });

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            const ETag = xhr.getResponseHeader("ETag");
            if (ETag !== null) {
              part.state = "complete";
              part.etag = ETag.replace(/"/g, "");
              this.onPartComplete();
            }
          } else {
            part.state = "failed";
            this.retryPart(part.partIndex);
          }
        }
      };

      const start = part.partIndex * chunkSize;
      const chunk = this.file.slice(start, start + chunkSize);
      xhr.send(chunk);
    }).then(() => {
      this.onPartComplete();
    });
  }

  abstract sendPartComplete(
    upload: MultipartUpload,
    partInputs: UploadPartInput[],
    preserveSectionStateForSections: number[]
  ): Promise<void>;

  async onPartComplete() {
    if (!this.upload) {
      throw new Error("Uploader.upload was missing");
    }

    if (this.isComplete() && this.overallState === "inflight") {
      const partInputs: UploadPartInput[] = this.parts.map((p) => ({
        etag: p.etag!,
        partNumber: p.partNumber,
      }));
      await this.sendPartComplete(this.upload, partInputs, this.preserveSectionStateForSections);
    }
  }

  onPartCompleteSuccess() {
    this.overallState = "complete";
    this.onComplete.forEach((onComplete) => onComplete());
  }

  onPartCompleteFailure(error: { status: number; message: string } | undefined) {
    if (error) {
      const { status, message } = error;
      if (status === 400) {
        let reason: Uploader["failureReason"];
        if (isInvalidDocumentError(message)) {
          reason = message;
        } else {
          reason = "unknown";
        }
        this.reportFailure(reason, message);
      } else if (status) {
        this.reportFailure("unknown", `HTTP ${status}`);
      }
    } else {
      this.reportFailure("unknown", "A network error occurred");
    }
  }

  getCurrentTotalSent(): number {
    return this.parts.reduce((sum, part) => sum + part.progress, 0);
  }

  isComplete(): boolean {
    return this.parts.every((p) => p.state === "complete");
  }

  getParts(): UploadPart[] {
    return this.parts;
  }
}
